import { Link } from '@/components/link/Link';
import { Loading } from '@/components/loading/Loading';
import {
  getActiveRoomsByTellerId,
  getFutureSchedulesByTellerId,
  getTeller,
} from '@raysee/firestore';
import { HeartBlackAndWhiteIcon, PhoneIcon } from '@raysee/icon';
import { getAgePeriod, getRating, getReviewsCount, getSex, Review } from '@raysee/schema';
import dayjs from 'dayjs';
import Image from 'next/legacy/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { StarRating, TellerImage } from '../../../components';
import CercleLinKButton from '../CercleLinKButton';

export default function ReviewPanel({ review }: { review: Review }) {
  const { data: teller, isLoading: isTellerLoading } = useSWR(
    ['teller', review.tellerId],
    () => getTeller(review.tellerId),
    { revalidateOnMount: true, revalidateOnFocus: false },
  );
  const { data: schedule } = useSWR(
    ['schedule', review.tellerId],
    () => getFutureSchedulesByTellerId(review.tellerId),
    { revalidateOnMount: true, revalidateOnFocus: false },
  );
  const { data: rooms } = useSWR(
    ['rooms', review.tellerId],
    () => getActiveRoomsByTellerId(review.tellerId),
    { revalidateOnMount: true, revalidateOnFocus: false },
  );

  const [isShowAllBtn, setIsShowAllBtn] = useState(false);
  const chatFilterRef = useRef<HTMLParagraphElement>(null);
  const [height, setHeight] = useState('h-20');

  useEffect(() => {
    if (chatFilterRef.current) {
      const isOver = chatFilterRef.current.offsetHeight < chatFilterRef.current.scrollHeight;
      setIsShowAllBtn(isOver);
      !isOver && setHeight('');
    }
  }, [isTellerLoading, teller]);

  const onShowAll = useCallback(() => {
    setHeight('');
    setIsShowAllBtn(false);
  }, []);

  if (isTellerLoading) {
    return <Loading size={12} color="border-t-rose-400" />;
  }
  return (
    !isTellerLoading &&
    teller && (
      <div className="p-4 shadow-md space-y-4">
        {/* <p className="mx-10 text-phone-primary font-serif font-bold text-center">{description}</p> */}

        <div>
          <Link href={`/tellers/${teller.id}`} legacyBehavior>
            <a>
              <div className="grid grid-cols-12">
                <div className="col-span-11">
                  <div className="flex">
                    <div className="items-center justify-center">
                      <TellerImage
                        teller={teller}
                        schedules={schedule}
                        rooms={rooms}
                        displayType="phone"
                        size={20}
                      />
                    </div>
                    <div className="ml-3">
                      <div className="flex items-center">
                        <p className="ml-1 mt-1 truncate text-base font-bold">{teller.name}先生</p>
                      </div>
                      <div className="mb-2 mt-2 flex items-center gap-1">
                        <div className="flex items-center gap-1">
                          <StarRating rating={getRating(teller)} size={13} />
                          <p className="text-xxs font-medium text-black">
                            ({getRating(teller)?.toFixed(2)})
                          </p>
                        </div>
                      </div>
                      <div className="flex">
                        <div className="flex min-w-[100px] items-center gap-1">
                          <div className="flex h-4 w-4 items-center justify-center rounded-sm bg-[#6B6B6B]">
                            <PhoneIcon width={12} height={12} />
                          </div>
                          <p className="text-xs font-semibold">
                            {teller.minPrice?.phone
                              ? `${new Intl.NumberFormat('ja-JP').format(
                                  teller.minPrice.phone,
                                )}円 / 分`
                              : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <HeartBlackAndWhiteIcon width={15} height={15} />
                          <p className="text-xs font-semibold">{getReviewsCount(teller)}件</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </a>
          </Link>
        </div>

        <div className="shadow">
          <div className="p-2">
            <div className="flex items-center space-x-3">
              <div className="w-10">
                <Image
                  src={`https://firebasestorage.googleapis.com/v0/b/luminous-dev.appspot.com/o/reviews%2Favator_${Math.floor(
                    Math.random() * 99,
                  )}.png?alt=media&token=c0b31b49-d66d-4e7c-8e43-fad840a8e824`}
                  alt="man"
                  className="rounded-full"
                  width={120}
                  height={120}
                />
              </div>
              <p className="font-bold">
                {review.user ? getAgePeriod(review.user.birthTime) : '20'}代
                {getSex(review.user?.sex ?? 'female')}
              </p>
            </div>
            <div className="flex items-center justify-between">
              <div style={{ position: 'relative' }}>
                <StarRating rating={review.rate} size={22} />
              </div>
              <p className="text-sm text-gray-600">
                {dayjs(review.createTime).format('YYYY/MM/DD')}
              </p>
            </div>
          </div>

          <div className="bg-gray-100 p-4 ">
            <p ref={chatFilterRef} className={`font-serif text-sm overflow-hidden ${height}`}>
              {review.comment}
            </p>
            {isShowAllBtn && (
              <div className=" flex justify-center">
                <button
                  type="button"
                  className="text-sm font-bold py-3 px-10 rounded-full inline-flex items-center justify-center flex-1  space-x-10"
                  onClick={onShowAll}
                >
                  全てを表示する&nbsp;&nbsp;
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 256 256"
                    className="rotate-90"
                  >
                    <title>more</title>
                    <path
                      fill="currentColor"
                      d="m184.49 136.49l-80 80a12 12 0 0 1-17-17L159 128L87.51 56.49a12 12 0 1 1 17-17l80 80a12 12 0 0 1-.02 17"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        <CercleLinKButton
          href={`/tellers/${review.tellerId}`}
          text={`${teller.name}先生の口コミを見る`}
          fillWidth
        />
      </div>
    )
  );
}
